import React from 'react';
import { Phone, AlertTriangle } from 'lucide-react';
import { SOFTPHONE_NOT_READY_MSG } from './powerDialConstants.js';
import { openPhonePanel } from '../PhonePanel.jsx';
import { powerdialApi } from '../../lib/powerdial.js';
import { usePowerDial } from '../../state/powerDial.js';

export default function ActiveSessionBanner({
  existingSessionId,
  cancellingExisting,
  busy,
  onCancelExistingAndRestart,
  onCloseModal,
  toast,
}) {
  if (!existingSessionId) return null;

  return (
    <div className="mb-4 p-3 rounded-xl border border-purple-500/40 bg-purple-500/10 text-xs text-purple-200">
      <div className="font-semibold mb-1 flex items-center gap-1.5 text-purple-100">
        <Phone className="w-3.5 h-3.5 text-civix-purple" />
        Active Session Detected
      </div>
      <p className="text-white/70 mb-2.5">
        An earlier Power Dial session is still marked open or queued. You can reset and start this new session immediately.
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={cancellingExisting || busy}
          onClick={onCancelExistingAndRestart}
          className="px-3 py-1.5 rounded-lg bg-red-500/30 hover:bg-red-500/50 text-red-100 font-semibold transition"
        >
          {cancellingExisting ? 'Cancelling…' : 'Cancel Old & Start New'}
        </button>
        <button
          type="button"
          onClick={async () => {
            try {
              let activeSession = null;
              const res = await powerdialApi.getActive();
              if (res?.session) {
                activeSession = res.session;
                if (activeSession.status === 'paused') {
                  const resumed = await powerdialApi.resume(activeSession._id).catch(() => null);
                  if (resumed?.session) activeSession = resumed.session;
                }
                usePowerDial?.getState?.().setSession?.(activeSession);
              }
            } catch (_) {}
            onCloseModal();
            toast?.success?.('Active session loaded in the bottom run bar');
          }}
          className="px-3 py-1.5 rounded-lg bg-emerald-500/25 hover:bg-emerald-500/40 text-emerald-100 border border-emerald-500/40 font-semibold transition"
        >
          Resume Active Session
        </button>
      </div>
    </div>
  );
}

export function WarnGateAlert({ warnGate, onSubmit }) {
  if (!warnGate) return null;

  return (
    <div className="mb-4 p-3 rounded-lg border border-amber-500/40 bg-amber-500/10 text-xs text-amber-200">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold mb-1">Number flagged Caution</div>
          <div>{warnGate.reason}</div>
          <button
            type="button"
            onClick={() => onSubmit(true)}
            className="mt-2 px-3 py-1.5 rounded-md bg-amber-500/30 hover:bg-amber-500/50 text-amber-100 font-semibold transition"
          >
            Continue anyway
          </button>
        </div>
      </div>
    </div>
  );
}

export function ModalErrorAlert({ error, onSwitchToOneLine, onClearError }) {
  if (!error) return null;

  return (
    <div className="mb-4 p-3 rounded-lg border border-red-500/40 bg-red-500/10 text-xs text-red-200">
      <div>{error}</div>
      {error === SOFTPHONE_NOT_READY_MSG && (
        <div className="mt-2.5 pt-2 border-t border-red-500/20 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onSwitchToOneLine}
            className="px-2.5 py-1 rounded bg-civix-purple/60 hover:bg-civix-purple text-white font-semibold transition"
          >
            Switch to 1 line (Starts Immediately)
          </button>
          <button
            type="button"
            onClick={() => { openPhonePanel(); onClearError?.(); }}
            className="px-2.5 py-1 rounded bg-white/10 hover:bg-white/20 text-white font-medium transition"
          >
            Open Phone Panel
          </button>
        </div>
      )}
    </div>
  );
}
